import { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AuthDialog() {
  const { authDialog, closeAuth, register, login } = useAuth();
  const { open, tab: initialTab, context } = authDialog;

  const [tab, setTab] = useState(initialTab);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setError("");
    }
  }, [open, initialTab]);

  if (!open) return null;

  const title = context?.title
    ? context.title
    : tab === "signup"
    ? "Create your free VantStride account"
    : "Welcome back";

  const subtitle =
    context?.subtitle ||
    (tab === "signup"
      ? "One free account unlocks every free resource, saves your AI chats, and stores your bookmarks."
      : "Sign in to your VantStride account.");

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (tab === "signup") {
        if (!fullName.trim()) throw new Error("Please enter your full name.");
        if (password.length < 8) throw new Error("Password must be at least 8 characters.");
        await register({ full_name: fullName.trim(), email: email.trim(), password });
        toast.success("Account created — welcome to VantStride.");
      } else {
        await login({ email: email.trim(), password });
        toast.success("Signed in.");
      }
      const cb = context?.onSuccess;
      closeAuth();
      if (typeof cb === "function") cb();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail ? formatApiError(detail) : err?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="auth-dialog"
    >
      <div
        className="absolute inset-0 bg-[#0A2540]/55 backdrop-blur-sm"
        onClick={submitting ? undefined : closeAuth}
        aria-hidden
      />
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-[0_30px_80px_rgba(10,37,64,0.25)] border border-slate-100 overflow-hidden">
        <button
          type="button"
          onClick={closeAuth}
          disabled={submitting}
          data-testid="auth-close"
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>

        <div className="p-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#0A2540] text-white grid place-items-center">
              <img
                src="/vs-monogram.png"
                alt=""
                aria-hidden="true"
                className="w-5 h-5 object-contain"
              />
            </div>
            <span className="font-display text-lg font-semibold text-[#0A2540]">VantStride</span>
          </div>
          <h3 className="font-display text-2xl text-[#0A2540] font-semibold mt-4">{title}</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{subtitle}</p>

          <div className="flex gap-2 rounded-full bg-slate-100 p-1 text-xs font-medium mt-6">
            <button
              type="button"
              onClick={() => setTab("signup")}
              data-testid="auth-tab-signup"
              className={`flex-1 py-2 rounded-full transition-colors ${
                tab === "signup" ? "bg-white text-[#0A2540] shadow-sm" : "text-slate-500"
              }`}
            >
              Create free account
            </button>
            <button
              type="button"
              onClick={() => setTab("login")}
              data-testid="auth-tab-login"
              className={`flex-1 py-2 rounded-full transition-colors ${
                tab === "login" ? "bg-white text-[#0A2540] shadow-sm" : "text-slate-500"
              }`}
            >
              Sign in
            </button>
          </div>

          <form onSubmit={handleAuthSubmit} className="mt-5 space-y-3" data-testid="auth-form">
            {tab === "signup" && (
              <div>
                <label className="text-xs font-medium text-slate-600" htmlFor="auth-name">
                  Full name
                </label>
                <input
                  id="auth-name"
                  data-testid="auth-fullname"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-slate-200 focus:border-[#0A2540] focus:outline-none px-4 py-3 text-sm bg-white"
                  placeholder="Your name"
                  required
                  autoComplete="name"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600" htmlFor="auth-email">
                Email
              </label>
              <input
                id="auth-email"
                type="email"
                data-testid="auth-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-slate-200 focus:border-[#0A2540] focus:outline-none px-4 py-3 text-sm bg-white"
                placeholder="you@school.edu"
                required
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600" htmlFor="auth-password">
                Password {tab === "signup" && <span className="text-slate-400">· min 8 chars</span>}
              </label>
              <input
                id="auth-password"
                type="password"
                data-testid="auth-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-slate-200 focus:border-[#0A2540] focus:outline-none px-4 py-3 text-sm bg-white"
                minLength={8}
                required
                autoComplete={tab === "signup" ? "new-password" : "current-password"}
              />
            </div>

            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-2xl px-3 py-2"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="auth-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0A2540] text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-[#051A2E] transition-colors active:scale-95 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Please wait…
                </>
              ) : tab === "signup" ? (
                "Create free account"
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <p className="mt-4 text-[11px] text-slate-400 text-center">
            By continuing you agree to our{" "}
            <a href="/terms" className="underline hover:text-[#0A2540]">
              Terms
            </a>{" "}
            and{" "}
            <a href="/privacy" className="underline hover:text-[#0A2540]">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
